/**
 * Heure « métier » de Défi Gourmand.
 *
 * Toutes les dates / heures de pilotage (filtres « aujourd'hui », deadline 10h, jour de
 * livraison par défaut, etc.) doivent être calculées dans le fuseau de l'activité
 * (Guyane française, UTC-3, sans heure d'été). Sinon un utilisateur basé à Paris
 * (UTC+1/+2) verrait un « aujourd'hui » différent de celui d'un opérateur en Guyane.
 *
 * Ces helpers ne touchent pas aux dates business déjà sérialisées en chaîne
 * "YYYY-MM-DD" : elles sont déjà neutres et restent affichées telles quelles via
 * `parseLocalDateOnly` (midi local) là où c'est nécessaire.
 */

/** Fuseau IANA de l'activité (Guyane française). */
export const BUSINESS_TIMEZONE = "America/Cayenne";

interface BusinessParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  second: number;
}

function getBusinessParts(date: Date = new Date()): BusinessParts {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: BUSINESS_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(date);
  const get = (t: string): number => {
    const found = parts.find((p) => p.type === t)?.value ?? "0";
    return Number(found);
  };
  const hour = get("hour");
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: hour === 24 ? 0 : hour,
    minute: get("minute"),
    second: get("second"),
  };
}

/** "YYYY-MM-DD" du jour business (Guyane), indépendant du fuseau du navigateur. */
export function getBusinessToday(): string {
  const p = getBusinessParts();
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

/**
 * Date « naïve » représentant maintenant en heure business — c'est-à-dire un objet `Date`
 * dont les composantes locales (`getFullYear`, `getHours`, …) correspondent à l'heure
 * Guyane. Pratique pour utiliser ensuite `format`, `setHours`, `isAfter` de date-fns
 * sans dépendance fuseau supplémentaire.
 *
 * À ne pas comparer à un autre `new Date()` natif : utiliser `getBusinessHour` et
 * `getBusinessTodayDate` pour les comparaisons « avant/après l'heure limite ».
 */
export function getBusinessNow(): Date {
  const p = getBusinessParts();
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

/** Heure business 0-23. */
export function getBusinessHour(): number {
  return getBusinessParts().hour;
}

/** Minutes business 0-59. */
export function getBusinessMinute(): number {
  return getBusinessParts().minute;
}

/** "HH:mm" en heure business. */
export function getBusinessTimeHHmm(): string {
  const p = getBusinessParts();
  return `${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}`;
}

/** Date locale (midi) correspondant au jour business — utile pour `format` + `addDays`. */
export function getBusinessTodayDate(): Date {
  const p = getBusinessParts();
  return new Date(p.year, p.month - 1, p.day, 12, 0, 0, 0);
}
